import { Callout } from '@/components/Elements';
import {
  ClockIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// セクションコンポーネント
type SectionProps = {
  icon: ReactNode;
  title: string;
  children: ReactNode;
};

const Section = ({ icon, title, children }: SectionProps) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 font-medium text-gray-700">
        <span className="size-5 text-blue-600">{icon}</span>
        <span>{title}</span>
      </div>
      <div className="ml-7">{children}</div>
    </div>
  );
};

type AlertCautionProps = {
  className?: string;
  hideOverwriteSettingNotice?: true;
};

export const AlertCaution = forwardRef<HTMLDivElement, AlertCautionProps>(
  ({ className, hideOverwriteSettingNotice }, ref) => {
    const { t } = useTranslation();

    return (
      <div
        ref={ref}
        className={clsx('space-y-6 text-sm text-gray-600', className)}
      >
        {/* 重要なお知らせ */}
        {!hideOverwriteSettingNotice && (
          <Callout variant="danger">
            <p className="font-bold">{t('重要なお知らせ')}</p>
            <p className="mt-1">
              {t(
                '通知設定は1つの検索条件に対してのみ有効です。他の検索条件で通知設定をオンにしている場合、それらの設定は自動的にオフになります。'
              )}
            </p>
          </Callout>
        )}

        {/* 通知機能の詳細 */}
        <div className="space-y-4">
          <Section icon={<ClockIcon />} title={t('通知タイミング')}>
            <p>
              {t(
                '毎朝8時以降、新しい記事が追加されたタイミングで通知します（1日最大1回）'
              )}
            </p>
          </Section>

          <Section icon={<MagnifyingGlassIcon />} title={t('通知対象の記事')}>
            <p className="mb-2">{t('以下の条件全てを満たす記事が対象です')}</p>
            <ol className="ml-4 list-decimal space-y-1">
              <li>{t('過去3日以内に検索可能になった記事')}</li>
              <li>{t('前回通知時以降に新しく追加された記事')}</li>
            </ol>
          </Section>

          <Section icon={<InformationCircleIcon />} title={t('ご注意')}>
            <p>
              {t(
                'グローバルな記事収集を行っているため、時差の影響により記事の公開から通知まで時間差が発生する場合がございます。'
              )}
            </p>
          </Section>
        </div>
      </div>
    );
  }
);

AlertCaution.displayName = 'AlertCaution';
